// Windows files open in inside desyr (14-viewers-join.md): pictures with zoom and dragging, text
// files highlighted with Prism, and a channel's topic. PDFs open in the browser's own viewer. The
// percentage chip's 100 % ↔ 200 % switch was a candidate to drop: the chip only shows the zoom.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, CopyButton, IconButton } from '../blocks/Button';
import { Dialog } from '../blocks/Dialog';
import { icons } from '../blocks/icons';
import { t } from '../i18n';
import { Plain } from './html';
import { chatIcons } from './icons';
import { languageOf, useHighlight } from './prism';

function Viewer({ name, tools, onClose, children }: { name: string; tools: ReactNode; onClose: () => void; children: ReactNode }) {
  return (
    <Dialog label={name || t('view.viewer')} onClose={onClose} className="viewer">
      <div className="viewer-bar" onContextMenu={(e) => e.stopPropagation()}>
        <IconButton label={t('view.close')} icon={icons.arrowBack} onClick={onClose} />
        <span className="viewer-name">{name}</span>
        {tools}
      </div>
      <div className="viewer-area" onContextMenu={(e) => e.stopPropagation()}>
        {children}
      </div>
    </Dialog>
  );
}

/** Saves a file under its name. */
export function saveAs(url: string, name: string) {
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
}

/** A picture opened big: fitted, zoom 20–500 % in steps of 20, dragged while zoomed (14, Image viewer). */
export function ImageViewer({ src, name, onClose }: { src: string; name: string; onClose: () => void }) {
  const [zoom, setZoom] = useState(100);
  const [at, setAt] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number }>(undefined);
  const step = (by: number) => setZoom((z) => (z + by >= 20 && z + by <= 500 ? z + by : z));
  useEffect(() => {
    if (zoom === 100) setAt({ x: 0, y: 0 });
  }, [zoom]);
  const download = async () => saveAs(src.startsWith('blob:') ? src : URL.createObjectURL(await (await fetch(src)).blob()), name);
  return (
    <Viewer
      name={name}
      onClose={onClose}
      tools={
        <div className="viewer-tools">
          <IconButton label={t('view.zoomOut')} icon={chatIcons.remove} className={zoom < 100 ? 'zoomed' : undefined} onClick={() => step(-20)} />
          <span className="chip zoom-chip">{zoom}%</span>
          <IconButton label={t('view.zoomIn')} icon={chatIcons.add} className={zoom > 100 ? 'zoomed' : undefined} onClick={() => step(20)} />
          <Button small variant="filled" onClick={() => void download()}>
            {t('view.download')}
          </Button>
        </div>
      }
    >
      <div
        className="viewer-picture"
        tabIndex={0}
        onWheel={(e) => step(e.deltaY < 0 ? 20 : -20)}
        onKeyDown={(e) => {
          if (e.key === '+' || e.key === '=') step(20);
          else if (e.key === '-') step(-20);
          else if (zoom !== 100 && e.key.startsWith('Arrow')) {
            const d = 40;
            setAt((p) => ({ x: p.x + (e.key === 'ArrowLeft' ? d : e.key === 'ArrowRight' ? -d : 0), y: p.y + (e.key === 'ArrowUp' ? d : e.key === 'ArrowDown' ? -d : 0) }));
          }
        }}
        onPointerDown={(e) => {
          if (zoom === 100) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX - at.x, y: e.clientY - at.y };
        }}
        onPointerMove={(e) => drag.current && setAt({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y })}
        onPointerUp={() => (drag.current = undefined)}
        style={{ cursor: zoom === 100 ? undefined : drag.current ? 'grabbing' : 'grab' }}
      >
        <img src={src} alt={name} draggable={false} style={{ transform: `translate(${at.x}px, ${at.y}px) scale(${zoom / 100})` }} />
      </div>
    </Viewer>
  );
}

const ENDINGS: Record<string, string> = { go: 'go', rs: 'rust', py: 'python', swift: 'swift', c: 'c', cpp: 'cpp', java: 'java', kt: 'kotlin', php: 'php', ts: 'typescript', js: 'javascript', jsx: 'javascript', tsx: 'typescript', html: 'markup', xhtml: 'markup', xht: 'markup', css: 'css', json: 'json', md: 'markdown', yaml: 'yaml', xml: 'markup', sql: 'sql' };

/** A text file: highlighted by its type or its name's ending, with "Copy text" (14, Text viewer). */
export function TextViewer({ text, name, type, onClose }: { text: string; name: string; type: string; onClose: () => void }) {
  const html = useHighlight(text, languageOf(`language-${type.split('/')[1]?.replace(/^x-/, '') ?? ''}`) ?? ENDINGS[name.split('.').pop()?.toLowerCase() ?? '']);
  return (
    <Viewer name={name} onClose={onClose} tools={<CopyButton text={text} label={t('view.copyText')} />}>
      <div className="code-block viewer-text">
        <pre>{html ? <code dangerouslySetInnerHTML={{ __html: html }} /> : <code>{text}</code>}</pre>
      </div>
    </Viewer>
  );
}

/** A channel's topic in full (14, Topic window). */
export function TopicViewer({ name, topic, onClose }: { name: string; topic: string; onClose: () => void }) {
  return (
    <Dialog title={name} onClose={onClose} className="topic-viewer">
      <IconButton className="dialog-close" label={t('common.close')} icon={icons.close} onClick={onClose} />
      <div className="text topic-text">
        <Plain text={topic} />
      </div>
    </Dialog>
  );
}
